import type { ReportQuestionItem } from '@/axios/company';
import React from 'react';
import { Spin, Table, Flex, Pagination, Space, Input } from 'antd';
import styles from './MobileReport.mobile.module.less';
import ReportLayout from '@/components/ReportLayout/ReportLayout';
import SVGIcon from '@/components/SVGIcon';
import ReactECharts from 'echarts-for-react';
import classNames from 'classnames';
import {
  searchReportTypes,
  summaryCards,
  getPlatformDistributionData,
  getPlatformDistributionOption,
  getReferenceSourceData,
  getReferenceSourceOption,
} from '../usePage';
import usePage from '../usePage';
import MobileReport from '@/pages/GEOReport/components/MobileReport';
import { useStore } from '@/stores';
import UpIcon from '../icons/up.svg?react';
import WordCloudCard from './WordCloudCard';

const MobileReportV2 = () => {
  // 图标位置预留
  // 数据全部取自 pageData
  const {
    isReady,
    pageData,
    keywordColumns,
    platforms,
    searchParams,
    setSearchParams,
    keywordTableData,
    answerModal,
  } = usePage();
  const { oemConfig, name, reportDetail } = pageData;
  const appStore = useStore('app');
  const referenceSourceData = getReferenceSourceData(reportDetail?.referenceSourceStatistics);
  const referenceSourceTotal = referenceSourceData.length;
  const platformDistributionData = getPlatformDistributionData({
    tableData: pageData.platformVisibleTableData,
    platformList: appStore.platformList,
  });
  /**
   * 为分页问题构建稳定的字符串行键，保持长 ID 精度。
   * @param {ReportQuestionItem} record 当前问题行。
   * @param {number} index 当前页行序号。
   * @returns {ReportQuestionItem & {__rowKey: string}} 包含展示行键的列表记录。
   */
  const keywordRecords = (keywordTableData.records || []).map((record: ReportQuestionItem, index: number) => ({
    ...record,
    __rowKey: `${searchParams.pageIndex}-${record.id || record.word || record.coreWord || 'keyword'}-${record.platform || 'platform'}-${index}`,
  }));

  const renderCard = (cardData: {
    title: string;
    key: string;
    icon: React.ReactNode;
    getValue: (data: any) => any;
  }) => {
    return (
      <>
        <div className={styles.cardTitle}>
          {cardData.title}
          <SVGIcon icon={cardData.icon} width={24} />
        </div>
        <Space>
          <div className={styles.cardValue}>{cardData.getValue(reportDetail) ?? '--'}</div>
          {(cardData.key === 'searchVisibility') && (
            <SVGIcon
              icon={<UpIcon />}
              style={{
                color: '#fff',
              }}
              width={16}
            />
          )}
        </Space>
      </>
    );
  };
  // return <div className="markdown-body">
  //     <ReactMarkdown>{content}</ReactMarkdown>
  //   </div>
  const isInIframe = window.self !== window.top;
  return isReady ? (
    <ReportLayout showNav={!isInIframe} oemConfig={oemConfig} name={name} dateInfo={pageData.dateInfo} reportDetail={reportDetail}>
      {/* 三个统计卡片 */}
      <div className={styles.cards}>
        {summaryCards.map((cardData, index) => (
          <div className={styles.card} key={index}>
            {renderCard(cardData)}
          </div>
        ))}
        <div className={styles.cardDivider}></div>
      </div>

      <div className={styles.chartSectionGrid}>
        {/* 平台可见词量分布 */}
        <div className={styles.platformSection}>
          <div className={styles.platformSectionTitle}>平台可见词量分布</div>
          <div className={styles.platformSectionSubtitle}>各平台词量占比</div>
          <div className={styles.platformSectionContent}>
            <div className={styles.pieChart}>
              <ReactECharts
                option={getPlatformDistributionOption({
                  isMobile: true,
                  data: platformDistributionData,
                })}
                className={styles.pieChartCanvas}
              />
            </div>
            <div className={styles.mobilePlatformLegend}>
              {platformDistributionData.map((item) => (
                <div className={styles.mobilePlatformLegendItem} key={item.name}>
                  <span style={{ backgroundColor: item.color }} />
                  <div className={styles.mobilePlatformLegendName}>{item.name}</div>
                  <strong>{item.value}%</strong>
                  <em>{item.count}词</em>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.referenceSection}>
          <div className={styles.platformSectionTitle}>引用信源平台</div>
          <div className={styles.platformSectionSubtitle}>AI回答引用来源统计</div>
          <div className={styles.referenceSectionContent}>
            <div className={styles.referenceChartWrap}>
              <ReactECharts
                option={getReferenceSourceOption({
                  data: referenceSourceData,
                })}
                className={styles.referenceChart}
                style={{ width: '100%', height: '100%' }}
              />
              <div className={styles.referenceChartCenter}>
                <div>高权重信源统计</div>
                <strong>{referenceSourceTotal}</strong>
              </div>
            </div>
            <div className={styles.referenceList}>
              {referenceSourceData.map((item) => (
                <div className={styles.referenceItem} key={item.name}>
                  <div className={styles.referenceName}>{item.name}</div>
                  <div className={styles.referenceBar}>
                    <div style={{ width: `${item.value}%`, backgroundColor: item.color }} />
                  </div>
                  <div className={styles.referenceRatio}>{item.value}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <WordCloudCard isMobile data={reportDetail?.positiveWordCloud} />
      <MobileReport pageData={pageData} />

      {/* 关键词搜索报表 Tabs+Table */}
      <div className={styles.keywordSection}>
        <div className={styles.keywordHeader}>
          <div className={styles.keywordTabs}>
            {searchReportTypes.map((tab) => (
              <div
                key={tab.value}
                className={classNames(styles.keywordTabItem, {
                  [styles.keywordTabItemActive]: searchParams.type === tab.value,
                })}
                onClick={() => {
                  setSearchParams({ type: tab.value, pageIndex: 1 });
                }}
              >
                {tab.label}
              </div>
            ))}
          </div>
          {/* 客户端类型切换 */}
          {/* <div className={styles.keywordClientTypes}>
            <Dropdown
              menu={{
                items: clientTypes.map((type) => ({
                  key: type.client,
                  label: (
                    <div
                      onClick={() => {
                        setSearchParams({ client: type.client });
                      }}
                    >
                      {type.name}
                    </div>
                  ),
                })),
              }}
            >
              <Space>
                {searchParams.client === 'PC' ? 'PC端' : '移动端'}
                <DownOutlined />
              </Space>
            </Dropdown>
          </div> */}
        </div>
        <div className={styles.keywordFilter}>
          <div className={styles.keywordPlatforms}>
            {platforms.map((platform) => (
              <div
                key={platform.platform}
                className={classNames(styles.keywordPlatformItem, {
                  [styles.keywordPlatformItemActive]: searchParams.platform === platform.platform,
                })}
                onClick={() => {
                  setSearchParams({ platform: platform.platform, pageIndex: 1 });
                }}
              >
                {platform.name}
              </div>
            ))}
          </div>
          <Input
            allowClear
            className={styles.keywordSearch}
            defaultValue={searchParams.search}
            onBlur={(event) => {
              const search = event.target.value.trim();
              if (search !== searchParams.search) {
                setSearchParams({ search, pageIndex: 1 });
              }
            }}
            onPressEnter={(event) => {
              const search = event.currentTarget.value.trim();
              if (search !== searchParams.search) {
                setSearchParams({ search, pageIndex: 1 });
              }
            }}
            placeholder="搜索问题"
          />
        </div>
        <div className={styles.keywordTable}>
          <Table
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={false}
            dataSource={keywordRecords}
            columns={keywordColumns}
            rowKey="__rowKey"
            rowHoverable={false}
            rowClassName={(record, index) => (index % 2 === 0 ? '' : styles.platformTableRowOdd)}
          />
          <Pagination
            align="center"
            current={searchParams.pageIndex}
            total={Number(keywordTableData.total)}
            pageSize={searchParams.pageSize}
            showSizeChanger={false}
            onChange={(curPage, pageSize) => {
              setSearchParams({
                pageIndex: curPage,
                pageSize,
              });
            }}
          />
        </div>
      </div>
      {answerModal}
    </ReportLayout>
  ) : (
    <div className={styles['geo_report_loading']}>
      <Spin fullscreen tip="加载中..." />
    </div>
  );
};

export default MobileReportV2;
